import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  adminApi,
  EmailAnalyticsDashboard,
  EmailTrends,
  EmailWorkerStatus,
} from '../utils/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

export default function EmailAnalytics() {
  const [days, setDays] = useState(30);
  const [trendPeriod, setTrendPeriod] = useState<'day' | 'week' | 'month'>('day');

  const { data: dashboard, isLoading: dashboardLoading } = useQuery<EmailAnalyticsDashboard>({
    queryKey: ['email-analytics-dashboard', days],
    queryFn: async () => {
      const response = await adminApi.getEmailAnalyticsDashboard(days);
      return response.data;
    },
    refetchInterval: 60000,
  });

  const { data: trends } = useQuery<EmailTrends>({
    queryKey: ['email-trends', trendPeriod],
    queryFn: async () => {
      const response = await adminApi.getEmailTrends(trendPeriod);
      return response.data;
    },
    refetchInterval: 60000,
  });

  const { data: workerStatus } = useQuery<EmailWorkerStatus>({
    queryKey: ['email-worker-status'],
    queryFn: async () => {
      const response = await adminApi.getEmailWorkerStatus();
      return response.data;
    },
    refetchInterval: 30000,
  });

  const handleBulkRetry = async () => {
    if (!window.confirm('Retry all failed email tasks?')) return;

    try {
      await adminApi.bulkRetryFailedEmails(50);
      alert('Bulk retry initiated successfully');
    } catch (error) {
      alert('Failed to bulk retry emails');
    }
  };

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const trendsChartData = trends
    ? {
        labels: trends.data_points.map((d) => new Date(d.date).toLocaleDateString()),
        datasets: [
          {
            label: 'Sent',
            data: trends.data_points.map((d) => d.sent),
            borderColor: 'rgb(34, 197, 94)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.3,
          },
          {
            label: 'Failed',
            data: trends.data_points.map((d) => d.failed),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.3,
          },
        ],
      }
    : null;

  const emailTypeData = dashboard
    ? {
        labels: Object.keys(dashboard.emails_by_type),
        datasets: [
          {
            data: Object.values(dashboard.emails_by_type),
            backgroundColor: [
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(251, 191, 36, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(139, 92, 246, 0.8)',
              'rgba(236, 72, 153, 0.8)',
            ],
          },
        ],
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Email Analytics</h1>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Overall Metrics */}
      {dashboard && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Emails</p>
              <p className="text-3xl font-bold text-gray-900">{dashboard.total_emails}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-3xl font-bold text-green-600">{dashboard.sent_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-3xl font-bold text-red-600">{dashboard.failed_count}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-3xl font-bold text-blue-600">
                {dashboard.success_rate_percent.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Performance & Queue Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Performance</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Avg Delivery Time</span>
                  <span className="text-sm font-medium">{dashboard.avg_delivery_time_seconds.toFixed(1)}s</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Today</span>
                  <span className="text-sm font-medium">{dashboard.emails_sent_today}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">This Week</span>
                  <span className="text-sm font-medium">{dashboard.emails_sent_this_week}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">This Month</span>
                  <span className="text-sm font-medium">{dashboard.emails_sent_this_month}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Queue Status</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Queue Depth</span>
                  <span className="text-sm font-medium">{dashboard.current_queue_depth}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Processing Rate</span>
                  <span className="text-sm font-medium">{dashboard.queue_processing_rate.toFixed(1)}/hr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Est. Clear Time</span>
                  <span className="text-sm font-medium">{dashboard.estimated_queue_clear_minutes}m</span>
                </div>
              </div>
            </div>

            {workerStatus && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="font-semibold text-gray-700 mb-4">Worker Status</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active Workers</span>
                    <span className="text-sm font-medium">
                      {workerStatus.workers_active}/{workerStatus.workers_total}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Redis</span>
                    <span
                      className={`text-sm font-medium ${
                        workerStatus.redis_healthy ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {workerStatus.redis_healthy ? 'Healthy' : 'Unhealthy'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Processing Lag</span>
                    <span className="text-sm font-medium">{workerStatus.processing_lag_seconds}s</span>
                  </div>
                  <button
                    onClick={handleBulkRetry}
                    className="mt-2 w-full px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Bulk Retry Failed
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Failure Analysis */}
          {dashboard.top_failure_reasons.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-700 mb-4">Top Failure Reasons</h3>
              <div className="space-y-2">
                {dashboard.top_failure_reasons.map((reason, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b last:border-0">
                    <span className="text-sm text-gray-600 flex-1 truncate pr-4">{reason.reason}</span>
                    <span className="text-sm font-medium text-red-600">{reason.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {trendsChartData && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-700">Email Volume Trends</h3>
              <select
                value={trendPeriod}
                onChange={(e) => setTrendPeriod(e.target.value as 'day' | 'week' | 'month')}
                className="px-3 py-1 border border-gray-300 rounded text-sm"
              >
                <option value="day">Daily (30 days)</option>
                <option value="week">Weekly (12 weeks)</option>
                <option value="month">Monthly (12 months)</option>
              </select>
            </div>
            <Line
              data={trendsChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { position: 'bottom' },
                },
                scales: {
                  y: { beginAtZero: true },
                },
              }}
            />
          </div>
        )}

        {emailTypeData && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-700 mb-4">Emails by Type</h3>
            <Doughnut
              data={emailTypeData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { position: 'bottom' },
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
